import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useAuth } from "../../hooks/useAuth";
import { useOrders } from "../../hooks/useOrders";
import AuthModal from "../subscribe-trial/components/AuthModal";
import { useNavigate } from "react-router-dom";

function computeProfit(order: {
  amount: number;
  selling_amount: number | null;
  return_rate: number | null;
  quantity?: number | null;
}): number {
  // 優先使用實際銷售金額，其次用 return_rate 推算
  if (order.selling_amount != null && order.selling_amount > 0) {
    return order.selling_amount - order.amount;
  }
  if (order.return_rate != null) {
    return Math.round(order.amount * (order.return_rate / 100));
  }
  return 0;
}

function OrderStatusBadge({ status }: { status: "active" | "completed" | "進行中" }) {
  if (status === "active" || status === "進行中") {
    return (
      <span className="inline-flex items-center gap-1 font-body text-[10px] font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
        <i className="ri-time-line text-[10px]" />
        進行中
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 font-body text-[10px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
      <i className="ri-check-double-line text-[10px]" />
      已完成
    </span>
  );
}

function OrderCard({
  order,
}: {
  order: {
    id: string;
    product_name: string | null;
    amount: number;
    selling_amount: number | null;
    return_rate: number | null;
    status: "active" | "completed" | "進行中";
    created_at: string;
    completion_percentage: number | null;
    quantity: number | null;
    image_url: string | null;
  };
}) {
  const isCompleted = order.status === "completed";
  const progress = isCompleted ? 100 : (order.completion_percentage ?? 0);
  const profit = computeProfit(order);
  const [imgError, setImgError] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden hover:border-stone-300 transition-all">
      <div className="flex flex-col sm:flex-row">
        {/* Image area */}
        <div className="w-full sm:w-36 md:w-40 h-36 sm:h-auto shrink-0 bg-stone-100 overflow-hidden flex items-center justify-center">
          {order.image_url && !imgError ? (
            <img
              src={order.image_url}
              alt={order.product_name || "商品圖片"}
              loading="lazy"
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <i className="ri-shopping-bag-3-line text-3xl text-stone-300" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-4 md:p-5">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h3 className="font-display text-sm md:text-base font-semibold text-stone-800 line-clamp-1">
              {order.product_name || "未知貨源"}
            </h3>
            <OrderStatusBadge status={order.status} />
          </div>

          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <p className="font-body text-[10px] text-stone-400 mb-0.5">認購金額</p>
              <p className="font-display text-sm font-bold text-stone-800">
                NT$ {order.amount.toLocaleString()}
                {order.quantity && order.quantity > 1 ? ` × ${order.quantity}` : ""}
              </p>
            </div>
            <div>
              <p className="font-body text-[10px] text-stone-400 mb-0.5">利潤</p>
              <p className="font-display text-sm font-bold text-emerald-600 flex items-center gap-0.5">
                <i className="ri-arrow-up-line text-[10px]" />
                NT$ {profit.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="font-body text-[10px] text-stone-400 mb-0.5">認購日期</p>
              <p className="font-body text-xs text-stone-600">
                {order.created_at?.slice(0, 10) || "--"}
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-1">
            <div className="flex items-center justify-between mb-2">
              <span className="font-body text-[11px] font-medium text-stone-600">
                認購完成度
              </span>
              <span className="font-body text-[11px] font-bold text-stone-800">
                {progress}%
              </span>
            </div>
            <div className="w-full h-3 rounded-full bg-[#E5E7EB] overflow-hidden">
              <div
                className={`h-full rounded-full transition-[width] duration-500 ease-out ${isCompleted ? "bg-emerald-500" : "bg-[#1e3a5f]"}`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-1.5">
              <span className="font-body text-[10px] text-stone-400">0%</span>
              <span className="font-body text-[10px] text-stone-400">100%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OrdersPage() {
  const { user, profile, isLoggedIn, loading: authLoading } = useAuth();
  const { orders, loading: ordersLoading } = useOrders();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [filter, setFilter] = useState<"all" | "active" | "completed">("all");

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 text-center">
          <i className="ri-loader-4-line text-3xl text-stone-300 animate-spin" />
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-28 px-4">
          <div className="max-w-md mx-auto text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-list-3-line text-2xl text-stone-400" />
            </div>
            <h2 className="font-display text-xl font-bold text-stone-800 mb-2">
              請先登入
            </h2>
            <p className="font-body text-sm text-stone-500 mb-6">
              登入後即可查看認購記錄與訂單狀態
            </p>
            <button
              onClick={() => setShowAuthModal(true)}
              className="font-body text-sm font-semibold px-6 py-3 rounded-full bg-rose-800 text-white hover:bg-rose-900 transition-colors"
            >
              立即登入
            </button>
          </div>
        </div>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => navigate("/orders")}
        />
      </div>
    );
  }

  const allOrders = orders;
  const filteredOrders =
    filter === "all"
      ? allOrders
      : filter === "active"
        ? allOrders.filter((o) => o.status === "active" || o.status === "進行中")
        : allOrders.filter((o) => o.status === "completed");

  const activeCount = allOrders.filter((o) => o.status === "active" || o.status === "進行中").length;
  const completedCount = allOrders.filter((o) => o.status === "completed").length;
  const totalInvested = allOrders.reduce((sum, o) => sum + o.amount * (o.quantity ?? 1), 0);

  const userName = profile?.name || user?.email?.split("@")[0] || "會員";

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-20 md:pt-24">
        {/* Page header */}
        <section className="bg-stone-900 py-10 md:py-14">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-rose-800 flex items-center justify-center shrink-0">
                  <i className="ri-file-list-3-line text-white" />
                </div>
                <div>
                  <h1 className="font-display text-xl md:text-2xl font-bold text-white">
                    我的認購記錄
                  </h1>
                  <p className="font-body text-xs text-stone-400">
                    {userName} · 共 {allOrders.length} 筆認購
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            {/* Stats summary */}
            <div id="order-stats" className="grid grid-cols-3 gap-3 md:gap-4 mb-8">
              <div className="bg-white rounded-2xl border border-stone-200 p-4 text-center">
                <p className="font-display text-xl font-bold text-stone-800">
                  {allOrders.length}
                </p>
                <p className="font-body text-[11px] text-stone-500 mt-1">總認購數</p>
              </div>
              <div className="bg-white rounded-2xl border border-stone-200 p-4 text-center">
                <p className="font-display text-xl font-bold text-amber-600">{activeCount}</p>
                <p className="font-body text-[11px] text-stone-500 mt-1">進行中</p>
              </div>
              <div className="bg-white rounded-2xl border border-stone-200 p-4 text-center">
                <p className="font-display text-xl font-bold text-stone-800">
                  NT$ {totalInvested.toLocaleString()}
                </p>
                <p className="font-body text-[11px] text-stone-500 mt-1">累計投入</p>
              </div>
            </div>

            {/* Filter tabs */}
            <div className="flex items-center gap-2 mb-6">
              <button
                onClick={() => setFilter("all")}
                className={`font-body text-xs font-semibold px-4 py-2 rounded-full transition-all whitespace-nowrap ${
                  filter === "all"
                    ? "bg-stone-900 text-white"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                全部 ({allOrders.length})
              </button>
              <button
                onClick={() => setFilter("active")}
                className={`font-body text-xs font-semibold px-4 py-2 rounded-full transition-all whitespace-nowrap ${
                  filter === "active"
                    ? "bg-amber-500 text-white"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                進行中 ({activeCount})
              </button>
              <button
                onClick={() => setFilter("completed")}
                className={`font-body text-xs font-semibold px-4 py-2 rounded-full transition-all whitespace-nowrap ${
                  filter === "completed"
                    ? "bg-emerald-600 text-white"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                已完成 ({completedCount})
              </button>
            </div>

            {/* Orders list */}
            {ordersLoading ? (
              <div className="text-center py-12">
                <i className="ri-loader-4-line text-3xl text-stone-300 animate-spin" />
              </div>
            ) : filteredOrders.length > 0 ? (
              <div className="flex flex-col gap-4">
                {filteredOrders.map((order) => (
                  <OrderCard key={order.id} order={order} />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
                <div className="w-12 h-12 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-file-list-3-line text-xl text-stone-400" />
                </div>
                <p className="font-body text-sm text-stone-500">暫無訂單記錄</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}